import React, { useCallback, useEffect, useState } from 'react';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import CursorPagination from 'components/CursorPagination';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { DEFAULT_PAGE_SIZE } from 'lib/page-size-options';
import { listRowNumber, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import {
  Plus,
  Trash2,
  Edit2,
  X,
  RefreshCw,
  Building2,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  User,
  Search,
  Filter,
  Check,
  Gamepad2,
  Layers,
  ListChecks,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ConsumerGameCatalogItem, ConsumerIntegration, Currency } from 'types/admin';
import {
  CONSUMER_PORTAL_MODULES,
  DEFAULT_CONSUMER_PORTAL_PERMISSIONS,
} from 'constants/consumer-portal';

const emptyForm = {
  consumerId: '',
  consumerName: '',
  secretKey: '',
  thirdPartyApiUrl: 'http://localhost:5001',
  defaultCurrencyCode: 'INR',
  status: true,
  regenerateSecret: false,
  portalUsername: '',
  portalPassword: '',
  portalPermissions: [...DEFAULT_CONSUMER_PORTAL_PERMISSIONS],
};

const maskSecret = (secret: string) => {
  if (!secret) return '—';
  if (secret.length <= 8) return '••••••••';
  return `${secret.slice(0, 4)}${'•'.repeat(Math.min(secret.length - 8, 12))}${secret.slice(-4)}`;
};

type ConsumerFilters = {
  consumerId: string;
  consumerName: string;
  portalUsername: string;
  status: '' | 'active' | 'inactive';
};

const emptyFilters: ConsumerFilters = {
  consumerId: '',
  consumerName: '',
  portalUsername: '',
  status: '',
};

const countActiveFilters = (filters: ConsumerFilters) =>
  (filters.consumerId.trim() ? 1 : 0) +
  (filters.consumerName.trim() ? 1 : 0) +
  (filters.portalUsername.trim() ? 1 : 0) +
  (filters.status ? 1 : 0);

const TH_CLASS =
  'px-5 py-3 text-left text-[10px] font-bold text-muted uppercase tracking-widest border-r border-line last:border-r-0 bg-subtle whitespace-nowrap';
const TH_NO_CLASS = `${TH_CLASS} w-14 text-center`;
const TD_CLASS = 'px-5 py-3.5 align-middle border-r border-line last:border-r-0';
const TD_NO_CLASS = `${TD_CLASS} whitespace-nowrap text-center text-sm font-medium text-muted tabular-nums`;

function buildConsumerQueryParams(filters: ConsumerFilters) {
  const params = new URLSearchParams();
  if (filters.consumerId.trim()) params.set('consumerId', filters.consumerId.trim());
  if (filters.consumerName.trim()) params.set('consumerName', filters.consumerName.trim());
  if (filters.portalUsername.trim()) params.set('portalUsername', filters.portalUsername.trim());
  if (filters.status) params.set('status', filters.status);
  return params;
}

const ConsumerManagement = () => {
  const navigate = useNavigate();
  const { confirm } = usePopup();
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [revealedSecrets, setRevealedSecrets] = useState<Record<number, boolean>>({});
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [draftFilters, setDraftFilters] = useState<ConsumerFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<ConsumerFilters>(emptyFilters);
  const [gamesModalConsumer, setGamesModalConsumer] = useState<ConsumerIntegration | null>(null);
  const [gameCatalog, setGameCatalog] = useState<ConsumerGameCatalogItem[]>([]);
  const [selectedGameIds, setSelectedGameIds] = useState<number[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesSaving, setGamesSaving] = useState(false);
  const [gamesError, setGamesError] = useState('');
  const [showPortalPassword, setShowPortalPassword] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState<Currency[]>([]);

  const isEditMode = editingId !== null;

  useEffect(() => {
    api.get<Currency[]>(API_ENDPOINTS.ADMIN.CURRENCIES.GET_ALL)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) setCurrencyOptions(res.data.filter((c) => c.status));
      })
      .catch(() => setCurrencyOptions([]));
  }, []);

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildConsumerQueryParams(appliedFilters), cursor, pageSize);
    const res = await api.get<ConsumerIntegration[]>(
      `${API_ENDPOINTS.ADMIN.CONSUMERS.GET_ALL}?${params.toString()}`
    );
    const meta = parseCursorPagination(res);
    return {
      items: res.success && Array.isArray(res.data) ? res.data : [],
      nextCursor: meta.nextCursor,
      hasMore: meta.hasMore,
      total: meta.total,
      totalPages: meta.totalPages,
    };
  }, [appliedFilters, pageSize]);

  const {
    page,
    items: consumers,
    loading,
    hasMore,
    total,
    totalPages,
    limit,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goToPage,
    goLast,
  } = useCursorListPage(fetchPage, pageSize);

  useEffect(() => {
    resetAndLoad().catch((err) => {
      console.error('Failed to fetch consumers:', err);
      setListError('Unable to reach the server. Restart api-backend after pulling latest changes.');
    });
  }, [appliedFilters, pageSize, resetAndLoad]);

  const fetchConsumers = async (silent = false) => {
    if (silent) setRefreshing(true);
    setListError('');
    try {
      await refreshCurrent();
    } catch (err) {
      console.error('Failed to fetch consumers:', err);
      setListError('Unable to reach the server. Restart api-backend after pulling latest changes.');
    } finally {
      setRefreshing(false);
    }
  };

  const activeFilterCount = countActiveFilters(appliedFilters);
  const isDatasetEmpty = consumers.length === 0 && !loading && !hasMore && activeFilterCount === 0;

  const updateDraft = (patch: Partial<ConsumerFilters>) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  };

  const applyFilters = () => setAppliedFilters({ ...draftFilters });

  const clearFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const closeModal = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowPortalPassword(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (consumer: ConsumerIntegration) => {
    setEditingId(consumer.id);
    setForm({
      consumerId: consumer.consumerId,
      consumerName: consumer.consumerName,
      secretKey: '',
      thirdPartyApiUrl: consumer.thirdPartyApiUrl,
      defaultCurrencyCode: consumer.defaultCurrencyCode || 'INR',
      status: consumer.status,
      regenerateSecret: false,
      portalUsername: consumer.portalUsername || '',
      portalPassword: '',
      portalPermissions:
        consumer.portalPermissions?.length
          ? [...consumer.portalPermissions]
          : [...DEFAULT_CONSUMER_PORTAL_PERMISSIONS],
    });
    setError('');
    setShowForm(true);
  };

  const closeGamesModal = () => {
    setGamesModalConsumer(null);
    setGameCatalog([]);
    setSelectedGameIds([]);
    setGamesError('');
  };

  const openGamesModal = async (consumer: ConsumerIntegration) => {
    setGamesModalConsumer(consumer);
    setGamesLoading(true);
    setGamesError('');
    try {
      const res = await api.get<{ catalog: ConsumerGameCatalogItem[] }>(
        API_ENDPOINTS.ADMIN.CONSUMERS.GET_GAMES(consumer.id)
      );
      if (res.success && res.data?.catalog) {
        setGameCatalog(res.data.catalog);
        setSelectedGameIds(res.data.catalog.filter((g) => g.assigned).map((g) => g.id));
      } else {
        setGamesError(res.message || 'Failed to load games.');
      }
    } catch {
      setGamesError('Failed to load game catalog.');
    } finally {
      setGamesLoading(false);
    }
  };

  const toggleGameSelection = (gameId: number) => {
    setSelectedGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  };

  const saveConsumerGames = async () => {
    if (!gamesModalConsumer) return;
    setGamesSaving(true);
    setGamesError('');
    try {
      const res = await api.put(API_ENDPOINTS.ADMIN.CONSUMERS.SET_GAMES(gamesModalConsumer.id), {
        gameIds: selectedGameIds,
      });
      if (res.success) {
        closeGamesModal();
        fetchConsumers(true);
      } else {
        setGamesError(res.message || 'Failed to save game assignments.');
      }
    } catch {
      setGamesError('Failed to save game assignments.');
    } finally {
      setGamesSaving(false);
    }
  };

  const togglePortalPermission = (key: string) => {
    setForm((prev) => ({
      ...prev,
      portalPermissions: prev.portalPermissions.includes(key)
        ? prev.portalPermissions.filter((p) => p !== key)
        : [...prev.portalPermissions, key],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const portalPayload = {
      portalUsername: form.portalUsername.trim() ? form.portalUsername.trim().toLowerCase() : undefined,
      ...(form.portalPassword.trim() ? { portalPassword: form.portalPassword.trim() } : {}),
      portalPermissions: form.portalPermissions,
    };

    try {
      const res = isEditMode
        ? await api.put(API_ENDPOINTS.ADMIN.CONSUMERS.UPDATE(editingId!), {
            consumerName: form.consumerName,
            thirdPartyApiUrl: form.thirdPartyApiUrl,
            defaultCurrencyCode: form.defaultCurrencyCode,
            status: form.status,
            ...portalPayload,
            ...(form.regenerateSecret
              ? { regenerateSecret: true }
              : form.secretKey.trim()
                ? { secretKey: form.secretKey.trim() }
                : {}),
          })
        : await api.post(API_ENDPOINTS.ADMIN.CONSUMERS.CREATE, {
            consumerId: form.consumerId,
            consumerName: form.consumerName,
            thirdPartyApiUrl: form.thirdPartyApiUrl,
            defaultCurrencyCode: form.defaultCurrencyCode,
            status: form.status,
            ...portalPayload,
            ...(form.secretKey.trim() ? { secretKey: form.secretKey.trim() } : {}),
          });

      if (res.success) {
        closeModal();
        fetchConsumers(true);
      } else {
        setError(res.message || (isEditMode ? 'Failed to update consumer.' : 'Failed to create consumer.'));
      }
    } catch {
      setError(isEditMode ? 'Error updating consumer.' : 'Error creating consumer.');
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Delete Consumer',
      message: 'Remove this consumer integration? Game launches using its secret key will stop working.',
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;

    try {
      const res = await api.delete(API_ENDPOINTS.ADMIN.CONSUMERS.DELETE(id));
      if (res.success) fetchConsumers(true);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const copySecret = async (id: number, secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const toggleReveal = (id: number) => {
    setRevealedSecrets((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-5 animate-fadeIn">
      {/* Header */}
      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        <div className="px-5 py-5 sm:px-7 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600 shrink-0">
              <Building2 size={22} strokeWidth={2.25} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-heading tracking-tight">Consumer Integrations</h1>
              <p className="text-sm text-muted mt-0.5">Register wallet consumers for external platform integrations.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-lg border border-line bg-subtle/60 px-2.5 py-1 text-xs font-semibold text-body">
              {consumers.length}{hasMore ? '+' : ''} loaded
            </span>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold hover:bg-opacity-95 text-brand-dark font-bold rounded-lg transition-all shadow-sm text-sm"
            >
              <Plus size={16} /> Add Consumer
            </button>
            <button
              type="button"
              onClick={() => fetchConsumers(true)}
              disabled={loading || refreshing}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading || refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {listError && (
        <div className="bg-red-500/10 text-brand-danger border border-red-100 px-4 py-3 rounded-xl text-sm font-semibold">
          {listError}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} sm:px-6 flex flex-col xl:flex-row xl:items-end gap-3`}>
          <div className="flex items-center gap-2 shrink-0 xl:pb-2">
            <Filter size={16} className="text-muted" />
            <span className="text-sm font-bold text-heading">Filters</span>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold text-[10px] font-bold">
                {activeFilterCount} active
              </span>
            )}
          </div>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 min-w-0">
            <div>
              <label className="block text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5">Consumer ID</label>
              <input
                type="text"
                placeholder="e.g. TP001"
                value={draftFilters.consumerId}
                onChange={(e) => updateDraft({ consumerId: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                className="w-full rounded-lg border border-line bg-card px-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30 font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5">Consumer name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={15} />
                <input
                  type="text"
                  placeholder="Search by name"
                  value={draftFilters.consumerName}
                  onChange={(e) => updateDraft({ consumerName: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyFilters();
                  }}
                  className="w-full rounded-lg border border-line bg-card pl-9 pr-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5">Portal username</label>
              <input
                type="text"
                placeholder="e.g. tp001"
                value={draftFilters.portalUsername}
                onChange={(e) => updateDraft({ portalUsername: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                className="w-full rounded-lg border border-line bg-card px-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30 font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5">Status</label>
              <select
                value={draftFilters.status}
                onChange={(e) => updateDraft({ status: e.target.value as ConsumerFilters['status'] })}
                className="w-full rounded-lg border border-line bg-card px-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={clearFilters}
              disabled={activeFilterCount === 0 && countActiveFilters(draftFilters) === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-line text-sm font-semibold text-muted hover:text-heading hover:bg-subtle transition-colors disabled:opacity-50"
            >
              <X size={14} />
              Clear
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-5 py-2 bg-brand-dark text-white rounded-lg text-sm font-bold hover:opacity-95 transition-opacity"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        {loading && consumers.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-muted font-medium">
            <RefreshCw className="animate-spin mr-2" size={18} /> Loading consumers...
          </div>
        ) : isDatasetEmpty ? (
          <div className="px-6 py-16 text-center">
            <Building2 size={32} className="mx-auto text-muted/30 mb-3" />
            <p className="font-semibold text-heading">No consumers registered yet</p>
            <p className="text-sm text-muted mt-1 max-w-md mx-auto">
              Add a consumer to issue secret keys and register their wallet API base URL.
            </p>
            <button
              type="button"
              onClick={openCreate}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark font-bold rounded-xl transition-all shadow-sm text-sm"
            >
              <Plus size={16} /> Add Consumer
            </button>
          </div>
        ) : (
          <>
            <div className={`${LIST_TD_CLASS} sm:px-6 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
              <div>
                <h4 className="font-bold text-heading flex items-center gap-2">
                  <ListChecks className="text-brand-gold" size={18} />
                  Registered Consumers
                </h4>
                <p className="text-xs text-muted mt-0.5">
                  {activeFilterCount > 0
                    ? `${consumers.length} match${consumers.length === 1 ? 'es' : ''} on this page`
                    : 'All registered consumers'}
                </p>
              </div>
              <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold shrink-0">
                {consumers.length} on page{hasMore ? ' · more' : ''}
              </span>
            </div>

            <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
              <table className={LIST_TABLE_CLASS}>
                <thead className={LIST_THEAD_CLASS}>
                  <tr>
                    <th className={TH_NO_CLASS}>No.</th>
                    <th className={TH_CLASS}>Consumer ID</th>
                    <th className={TH_CLASS}>Name</th>
                    <th className={TH_CLASS}>Currency</th>
                    <th className={`${TH_CLASS} min-w-[200px]`}>Wallet API URL</th>
                    <th className={`${TH_CLASS} min-w-[220px]`}>Secret Key</th>
                    <th className={`${TH_CLASS} min-w-[160px]`}>Portal Username</th>
                    <th className={TH_CLASS}>Games</th>
                    <th className={TH_CLASS}>Status</th>
                    <th className={`${TH_CLASS} w-28`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={LIST_TBODY_CLASS}>
                  {consumers.length > 0 ? (
                    consumers.map((c, index) => (
                      <tr key={c.id} className="group hover:bg-subtle/40 transition-colors">
                        <td className={TD_NO_CLASS}>{listRowNumber(page, index, limit)}</td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <span className="inline-flex items-center rounded-md border border-brand-gold/25 bg-brand-gold/10 px-2 py-1 font-mono text-xs font-bold text-brand-gold">
                            {c.consumerId}
                          </span>
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => navigate(`/consumers/${c.id}`)}
                            className="text-sm font-semibold text-heading hover:text-brand-gold hover:underline transition-colors text-left"
                            title="Open consumer details"
                          >
                            {c.consumerName}
                          </button>
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <span
                            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-subtle/60 px-2.5 py-1 text-sm font-semibold text-heading tabular-nums"
                            title={c.defaultCurrencyCode || 'Not set'}
                          >
                            {c.currencySymbol ? (
                              <span className="text-base leading-none">{c.currencySymbol}</span>
                            ) : null}
                            <span className="font-mono text-xs">{c.defaultCurrencyCode || '—'}</span>
                          </span>
                        </td>
                        <td className={`${TD_CLASS} max-w-[240px]`}>
                          <a
                            href={c.thirdPartyApiUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group/link inline-flex max-w-full items-center gap-1.5 rounded-lg border border-line bg-subtle/50 px-2.5 py-1.5 font-mono text-xs text-body hover:border-brand-gold/40 hover:text-heading transition-colors"
                            title={c.thirdPartyApiUrl}
                          >
                            <span className="truncate">{c.thirdPartyApiUrl}</span>
                            <ExternalLink size={12} className="shrink-0 text-muted group-hover/link:text-heading" />
                          </a>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="inline-flex max-w-full items-center gap-1 rounded-lg border border-line bg-subtle/50 px-2 py-1.5">
                            <KeyRound size={13} className="text-faint shrink-0" />
                            <span className="font-mono text-xs text-body truncate max-w-[140px] sm:max-w-[180px]">
                              {revealedSecrets[c.id] ? c.secretKey : maskSecret(c.secretKey)}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleReveal(c.id)}
                              className="p-1 text-muted hover:text-heading rounded transition-colors"
                              title={revealedSecrets[c.id] ? 'Hide secret' : 'Reveal secret'}
                            >
                              {revealedSecrets[c.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => copySecret(c.id, c.secretKey)}
                              className="p-1 text-muted hover:text-heading rounded transition-colors"
                              title="Copy secret key"
                            >
                              {copiedId === c.id ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                            </button>
                          </div>
                        </td>
                        <td className={TD_CLASS}>
                          <div
                            className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-line bg-subtle/50 px-2 py-1.5"
                            title={
                              c.hasPortalPassword
                                ? 'Portal login configured'
                                : 'Portal password not set'
                            }
                          >
                            <User size={13} className="text-faint shrink-0" />
                            <span className="font-mono text-xs font-semibold text-heading truncate max-w-[100px] sm:max-w-[120px]">
                              {c.portalUsername?.trim() || '—'}
                            </span>
                            <span className="w-px h-4 bg-line shrink-0" aria-hidden />
                            {c.hasPortalPassword ? (
                              <span className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-bold uppercase text-emerald-700">
                                <Check size={11} strokeWidth={2.5} />
                                Pwd
                              </span>
                            ) : (
                              <span className="shrink-0 text-[10px] font-bold uppercase text-amber-700">
                                No pwd
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => openGamesModal(c)}
                            className="inline-flex items-center gap-2 rounded-lg border border-line bg-subtle/50 px-2.5 py-1.5 text-xs font-semibold text-heading hover:border-brand-gold/40 transition-colors"
                            title="Manage assigned games"
                          >
                            <Layers size={14} className="text-brand-gold" />
                            {c.gameCount ?? 0} game{(c.gameCount ?? 0) === 1 ? '' : 's'}
                          </button>
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <span
                            className={`px-2 py-0.5 inline-flex text-[10px] font-bold rounded-md uppercase tracking-wide ${
                              c.status ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'
                            }`}
                          >
                            {c.status ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => navigate(`/consumers/${c.id}`)}
                              className="inline-flex items-center justify-center p-2 bg-subtle hover:bg-subtle text-heading border border-line rounded-lg transition-all"
                              title="View consumer details"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEdit(c)}
                              className="inline-flex items-center justify-center p-2 bg-subtle hover:bg-subtle text-heading border border-line rounded-lg transition-all"
                              title="Edit consumer"
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(c.id)}
                              className="inline-flex items-center justify-center p-2 text-brand-danger hover:bg-red-500/10 border border-line rounded-lg transition-all"
                              title="Delete consumer"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="px-5 py-14 text-center">
                        <Building2 size={28} className="mx-auto text-muted/30 mb-2" />
                        <p className="font-semibold text-body">No consumers match your filters</p>
                        <p className="text-sm text-muted mt-1">Adjust filters or clear them to see all consumers.</p>
                        {activeFilterCount > 0 && (
                          <button
                            type="button"
                            onClick={clearFilters}
                            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-line text-sm font-semibold text-muted hover:text-heading hover:bg-subtle transition-colors"
                          >
                            <X size={14} /> Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <CursorPagination
              page={page}
              totalPages={totalPages}
              total={total}
              itemCount={consumers.length}
              limit={limit}
              hasMore={hasMore}
              loading={loading || refreshing}
              onFirst={goFirst}
              onPageChange={goToPage}
              onLast={goLast}
              onLimitChange={setPageSize}
            />
          </>
        )}
      </div>

      {gamesModalConsumer && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn"
          role="dialog"
          aria-modal="true"
          aria-label="Assign games"
        >
          <div
            className="absolute inset-0 bg-black/65"
            aria-hidden
            onClick={closeGamesModal}
          />
          <div
            className="relative z-10 w-full max-w-lg max-h-[min(92dvh,720px)] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden animate-scaleIn flex flex-col ring-1 ring-black/10"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white shrink-0">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">Assign Games</h4>
                <p className="text-xs text-faint">
                  {gamesModalConsumer.consumerName} ({gamesModalConsumer.consumerId})
                </p>
              </div>
              <button type="button" onClick={closeGamesModal} className="text-muted hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-4 bg-card min-h-0">
              {gamesError && (
                <div className="bg-red-500/10 text-brand-danger border border-red-100 px-4 py-3 rounded-xl text-sm font-semibold">
                  {gamesError}
                </div>
              )}

              <p className="text-sm text-muted">
                Select which platform games this consumer can launch. Each consumer may have one or more games.
              </p>

              {gamesLoading ? (
                <div className="flex h-32 items-center justify-center text-muted">
                  <RefreshCw className="animate-spin mr-2" size={18} /> Loading games...
                </div>
              ) : gameCatalog.length === 0 ? (
                <div className="rounded-xl border border-line bg-subtle/40 p-6 text-center">
                  <Gamepad2 size={28} className="mx-auto text-muted/40 mb-2" />
                  <p className="font-semibold text-heading">No games registered</p>
                  <p className="text-sm text-muted mt-1">Add games in the Game Registry module first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {gameCatalog.map((game) => {
                    const checked = selectedGameIds.includes(game.id);
                    return (
                      <label
                        key={game.id}
                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                          checked ? 'border-brand-gold/50 bg-brand-gold/5' : 'border-line hover:bg-subtle/40'
                        } ${!game.status ? 'opacity-60' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!game.status}
                          onChange={() => toggleGameSelection(game.id)}
                          className="mt-1 h-4 w-4 rounded border-line text-brand-gold focus:ring-brand-gold"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-heading">{game.name}</span>
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-subtle text-brand-gold border border-line">
                              {game.gameId}
                            </span>
                            {!game.status && (
                              <span className="text-[10px] font-bold uppercase text-red-700 bg-red-500/10 px-1.5 py-0.5 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted mt-1 font-mono truncate">{game.backendUrl}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-line bg-card shrink-0 flex gap-3">
              <button
                type="button"
                onClick={closeGamesModal}
                className="flex-1 px-4 py-2.5 bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveConsumerGames}
                disabled={gamesSaving || gamesLoading}
                className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm disabled:opacity-50"
              >
                {gamesSaving ? 'Saving…' : `Save (${selectedGameIds.length} selected)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn"
          role="dialog"
          aria-modal="true"
          aria-label={isEditMode ? 'Edit consumer' : 'Register consumer'}
        >
          <div className="absolute inset-0 bg-black/65" aria-hidden onClick={closeModal} />
          <div
            className="relative z-10 w-full max-w-lg max-h-[min(92dvh,720px)] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden animate-scaleIn flex flex-col ring-1 ring-black/10"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white shrink-0">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">
                  {isEditMode ? 'Edit Consumer' : 'Register Consumer'}
                </h4>
                <p className="text-xs text-faint">Wallet API URL and authentication secret</p>
              </div>
              <button type="button" onClick={closeModal} className="text-muted hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <form
              id="consumer-form"
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-4 bg-card min-h-0"
            >
              {error && (
                <div className="bg-red-500/10 text-brand-danger border border-red-100 px-4 py-3 rounded-xl text-sm font-semibold">
                  {error}
                </div>
              )}

              {!isEditMode && (
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Consumer ID</label>
                  <input
                    type="text"
                    required
                    placeholder="TP001"
                    maxLength={10}
                    value={form.consumerId}
                    onChange={(e) =>
                      setForm({ ...form, consumerId: e.target.value.trim().toUpperCase().replace(/\s+/g, '') })
                    }
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border font-mono"
                  />
                  <p className="text-[11px] text-muted mt-1">Unique identifier — cannot be changed after creation.</p>
                </div>
              )}

              {isEditMode && (
                <div className="p-4 bg-subtle rounded-xl border border-line">
                  <p className="text-xs font-bold text-muted uppercase tracking-wider mb-1">Consumer ID</p>
                  <p className="font-mono text-sm font-semibold text-brand-gold">{form.consumerId}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Consumer Name</label>
                <input
                  type="text"
                  required
                  placeholder="Fantasy Consumer"
                  value={form.consumerName}
                  onChange={(e) => setForm({ ...form, consumerName: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Wallet API Base URL</label>
                <input
                  type="url"
                  required
                  placeholder="http://localhost:5001"
                  value={form.thirdPartyApiUrl}
                  onChange={(e) => setForm({ ...form, thirdPartyApiUrl: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border font-mono text-sm"
                />
                <p className="text-[11px] text-muted mt-1">
                  Consumer server base URL — game backend calls wallet APIs under this host.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Default wallet currency</label>
                <select
                  required
                  value={form.defaultCurrencyCode}
                  onChange={(e) => setForm({ ...form, defaultCurrencyCode: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border bg-card"
                >
                  {currencyOptions.length > 0 ? (
                    currencyOptions.map((c) => (
                      <option key={c.id} value={c.code}>
                        {c.symbol} {c.code} — {c.name}
                      </option>
                    ))
                  ) : (
                    <option value={form.defaultCurrencyCode}>{form.defaultCurrencyCode}</option>
                  )}
                </select>
                <p className="text-[11px] text-muted mt-1">
                  Fallback when the consumer wallet API does not return a user currency. Must match a code registered in Game Configuration.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">
                  Secret Key {isEditMode ? '(optional)' : '(optional — auto-generated if blank)'}
                </label>
                <input
                  type="text"
                  placeholder={isEditMode ? 'Leave blank to keep current secret' : 'Auto-generated if left blank'}
                  value={form.secretKey}
                  onChange={(e) => setForm({ ...form, secretKey: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border font-mono text-sm"
                />
                {isEditMode && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-muted">
                    <input
                      type="checkbox"
                      checked={form.regenerateSecret}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          regenerateSecret: e.target.checked,
                          secretKey: e.target.checked ? '' : form.secretKey,
                        })
                      }
                      className="h-4 w-4 rounded border-line text-brand-gold focus:ring-brand-gold"
                    />
                    Generate a new secret key on save
                  </label>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-subtle rounded-xl border border-line">
                <div>
                  <h5 className="font-semibold text-sm text-heading">Active</h5>
                  <p className="text-xs text-muted">Inactive consumers cannot authenticate game launches.</p>
                </div>
                <input
                  type="checkbox"
                  checked={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.checked })}
                  className="h-5 w-5 rounded border-line text-brand-gold focus:ring-brand-gold cursor-pointer"
                />
              </div>

              <div className="border-t border-line pt-4 space-y-4">
                <h5 className="font-semibold text-sm text-heading">Consumer Portal Login</h5>
                <p className="text-xs text-muted -mt-2">
                  Credentials for the consumer panel at{' '}
                  <code className="text-[11px] bg-subtle px-1 py-0.5 rounded">/consumer/login</code>{' '}
                  (e.g. http://localhost:3000/consumer/login).
                </p>

                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Portal Username</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" size={16} />
                    <input
                      type="text"
                      placeholder="e.g. tp001"
                      autoComplete="off"
                      value={form.portalUsername}
                      onChange={(e) => setForm({ ...form, portalUsername: e.target.value })}
                      className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm py-3 pl-10 pr-3 border font-mono"
                    />
                  </div>
                  <p className="text-[11px] text-muted mt-1">
                    Login username for{' '}
                    <span className="font-mono text-heading">/consumer/login</span>
                    {isEditMode && form.portalUsername.trim() && (
                      <> — saved as <span className="font-mono text-heading">{form.portalUsername.trim().toLowerCase()}</span></>
                    )}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">
                    Portal Password {isEditMode ? '(leave blank to keep current)' : '(optional)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPortalPassword ? 'text' : 'password'}
                      placeholder={isEditMode ? '••••••••' : 'Min 6 characters'}
                      value={form.portalPassword}
                      onChange={(e) => setForm({ ...form, portalPassword: e.target.value })}
                      className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 pr-11 border"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPortalPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-heading rounded transition-colors"
                      aria-label={showPortalPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showPortalPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Portal Permissions</label>
                  <div className="grid grid-cols-2 gap-2">
                    {CONSUMER_PORTAL_MODULES.map((mod) => (
                      <label
                        key={mod.key}
                        className="flex items-center gap-2 p-2 rounded-lg border border-line bg-subtle/40 text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.portalPermissions.includes(mod.key)}
                          onChange={() => togglePortalPermission(mod.key)}
                          className="h-4 w-4 rounded border-line text-brand-gold focus:ring-brand-gold"
                        />
                        {mod.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </form>

            <div className="p-6 border-t border-line bg-card shrink-0 flex gap-4">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="consumer-form"
                className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm"
              >
                {isEditMode ? 'Save Changes' : 'Register Consumer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsumerManagement;
